import { api, apiRequest } from '@/core/http/apiClient';
import { download } from '@/core/http/files';
import { newIdempotencyKey, postFile } from '@/core/http/helpers';
import type { ExportFormatCode } from '@/core/models/practice.models';
import type {
  BulkEmailReportsPayload,
  BulkEmailReportsResponse,
  CashFlowReport,
  EmailDraft,
  EmailLogQuery,
  EmailLogRow,
  EmailSendResponse,
  NominalLedgerReport,
  ReportDetail,
  ReportFormOptions,
  ReportOptions,
  ReportRow,
  ReportSourceLineage,
  ReportValidationCheck,
  ReportValidationResult,
  SaveReportOptionsPayload,
  SaveReportPayload,
  SendEmailPayload,
  TrialBalanceOption,
  TrialBalanceReport,
} from '@/core/models/reports.models';

export const reportsService = {
  /** The client's trial balances (pick list for the report screens). */
  getTrialBalances: (clientId: string) => api.get<TrialBalanceOption[]>(`TrialBalance/getTrialBalances/${clientId}`),

  // ---- Trial balance report ----

  getTrialBalanceReport: (trialBalanceId: string) =>
    api.get<TrialBalanceReport>(`Reports/getTrialBalanceReport/${trialBalanceId}`),

  exportTrialBalanceReport: (trialBalanceId: string, format: ExportFormatCode) =>
    postFile(`Reports/exportTrialBalanceReport/${trialBalanceId}`, { format }),

  sendTrialBalance: (trialBalanceId: string, payload: SendEmailPayload) =>
    api.post<EmailSendResponse>(`Reports/sendTrialBalance/${trialBalanceId}`, payload),

  // ---- Nominal ledger ----

  getNominalLedger: (trialBalanceId: string) =>
    api.get<NominalLedgerReport>(`Reports/getNominalLedger/${trialBalanceId}`),

  exportNominalLedger: (trialBalanceId: string, format: ExportFormatCode) =>
    postFile(`Reports/exportNominalLedger/${trialBalanceId}`, { format }),

  // ---- Cash flows ----

  getCashFlow: (trialBalanceId: string, previousTrialBalanceId: string | null) => {
    const params: Record<string, string> = previousTrialBalanceId ? { previousTrialBalanceId } : {};
    return api.get<CashFlowReport>(`Reports/getCashFlow/${trialBalanceId}`, { params });
  },

  exportCashFlow: (trialBalanceId: string, previousTrialBalanceId: string | null, format: ExportFormatCode) =>
    postFile(`Reports/exportCashFlow/${trialBalanceId}`, { format, previousTrialBalanceId }),

  // ---- Annual and management accounts ----

  getReports: (clientId: string, reportType: number) =>
    api.get<ReportRow[]>(`Reports/getReports/${clientId}/${reportType}`),

  getReportFormOptions: (clientId: string, reportType: number) =>
    api.get<ReportFormOptions>(`Reports/getReportFormOptions/${clientId}/${reportType}`),

  getReport: (id: string) => api.get<ReportDetail>(`Reports/getReport/${id}`),

  /** APT342: the report's data lineage and audit trail - the latest capture, the history of every generation, and the journals posted since. */
  getReportSource: (id: string) => api.get<ReportSourceLineage>(`Reports/getSource/${id}`),

  saveReport: (payload: SaveReportPayload) => api.post<ReportDetail>('Reports/saveReport', payload),

  /**
   * BUG-048: Generate - the filing checks of the report Create New Report describes, before it is saved (validateReport
   * for an unsaved report); nothing is recorded.
   */
  validateReportDraft: (payload: SaveReportPayload) =>
    api.post<ReportValidationResult>('Reports/validateReportDraft', payload),

  /**
   * BUG-048: Generate - the PDF of the report Create New Report describes, checked as saveReport checks it, with nothing
   * saved. The preview's Save Report then calls saveReport (as the old application).
   */
  previewReportDraft: (payload: SaveReportPayload) =>
    api.post<Blob>('Reports/previewReportDraft', payload, { responseType: 'blob' }),

  deleteReports: (ids: string[]) => api.post<boolean>('Reports/deleteReports', ids),

  changeReportStatus: (id: string) => api.post<ReportRow>(`Reports/changeReportStatus/${id}`, {}),

  /** Reruns the document build against the report's current trial balance/report settings - nothing is cached,
   *  so this both confirms the report can still be produced and refreshes its "Last Updated" timestamp. */
  regenerateReport: (id: string) => api.post<ReportRow>(`Reports/regenerateReport/${id}`, {}),

  /** Idempotent server-side, so a retried click cannot create a second signing envelope. */
  sendToCapisign: (id: string) =>
    api.post<ReportRow>(`Reports/sendToCapisign/${id}`, {}, { headers: { 'Idempotency-Key': newIdempotencyKey() } }),

  clearCapisignStatus: (id: string) => api.post<ReportRow>(`Reports/clearCapisignStatus/${id}`, {}),

  validateReport: (id: string) => api.post<ReportValidationResult>(`Reports/validateReport/${id}`, {}),

  downloadReport: async (id: string) => {
    const response = await apiRequest<Blob>('GET', `Reports/downloadReport/${id}`, { responseType: 'blob' });
    download(response);
    return true;
  },

  /**
   * BUG-049: the report's inline XBRL (XHTML), tagged from the figures the report itself prints - the same build as
   * downloadReport()'s PDF.
   */
  downloadReportIxbrl: async (id: string) => {
    const response = await apiRequest<Blob>('GET', `Reports/downloadReportIxbrl/${id}`, { responseType: 'blob' });
    download(response);
    return true;
  },

  /** BUG-062: the CIC34 community interest company report on its own. */
  downloadCicReport: async (id: string) => {
    download(await apiRequest<Blob>('GET', `Reports/downloadCicReport/${id}`, { responseType: 'blob' }));
    return true;
  },

  /**
   * BUG-062: the Companies House package - the accounts and the CIC34 report - as one zip. The CIC34 report comes from
   * `cicReportId` when another CIC report is chosen (86478-86480); the server refuses one of another accounting period.
   */
  downloadCicPackage: async (id: string, cicReportId?: string) => {
    download(
      await apiRequest<Blob>('GET', `Reports/downloadCicPackage/${id}`, {
        responseType: 'blob',
        params: { cicReportId },
      }),
    );
    return true;
  },

  /** BUG-062: what stands in the way of the CIC package - the report's checks and the CIC34 parts still to complete. */
  getCicPackageChecks: (id: string, cicReportId?: string) =>
    api.get<ReportValidationCheck[]>(`Reports/getCicPackageChecks/${id}`, { params: { cicReportId } }),

  /** BUG-062: emails the CIC34 report PDF. */
  sendCicReport: (id: string, payload: SendEmailPayload) =>
    api.post<EmailSendResponse>(`Reports/sendCicReport/${id}`, payload),

  /**
   * The report preview: the whole report as its iXBRL (each tagged fact shaded where it prints, as the old application's
   * preview), or the same pages untagged when the report has no iXBRL - an XHTML document, as a blob.
   */
  previewReportHtml: (id: string) => api.get<Blob>(`Reports/previewReportHtml/${id}`, { responseType: 'blob' }),

  /** The same PDF as downloadReport(), returned as a blob instead of being saved. */
  previewReport: (id: string) => api.get<Blob>(`Reports/downloadReport/${id}`, { responseType: 'blob' }),

  getReportEmailDraft: (id: string) => api.get<EmailDraft>(`Reports/getReportEmailDraft/${id}`),

  sendReport: (id: string, payload: SendEmailPayload) =>
    api.post<EmailSendResponse>(`Reports/sendReport/${id}`, payload),

  /** Downloads one zip holding the PDF of every selected report. */
  bulkDownloadReports: (reportIds: string[]) => postFile('BulkReports/downloadReports', { reportIds }),

  /** Emails each selected report; the response has a result per report. */
  bulkEmailReports: (payload: BulkEmailReportsPayload) =>
    api.post<BulkEmailReportsResponse>('BulkReports/emailReports', payload),

  // ---- Report options ----

  /** reportType 1 reads the Management Accounts options, 0 the Annual Accounts ones. */
  getReportOptions: (clientId: string, reportType = 0) =>
    api.get<ReportOptions>(`Reports/getReportOptions/${clientId}`, { params: { reportType } }),

  saveReportOptions: (payload: SaveReportOptionsPayload) =>
    api.post<ReportOptions>('Reports/saveReportOptions', payload),

  // ---- Email log ----

  getEmailLogs: (query: EmailLogQuery) => api.post<EmailLogRow[]>('EmailLog/getEmailLogs', query),

  exportEmailLogs: (query: EmailLogQuery, format: ExportFormatCode, ids: string[]) =>
    postFile('EmailLog/exportEmailLogs', { ...query, format, ids }),
};
